// Copyright ©️ 2026 eVolve MEP, LLC

import { createContext, useCallback, useState, useEffect, type ReactNode, useRef, useMemo } from 'react';

import { LoadingOverlay } from '@mantine/core';
import { differenceInMinutes } from 'date-fns';
// oxlint-disable-next-line no-restricted-imports
import { useSearchParams } from 'react-router';
import { v4 as uuid } from 'uuid';

import { strongIsEqual } from 'helpers/strongEntries';
import { useGeneralContext } from 'helpers/useGeneralContext';

type AppNeedsRefreshContextType = {
  updateAvailable: boolean;
  hardRefresh: () => void;
};

const AppNeedsRefreshContext = createContext<AppNeedsRefreshContextType | undefined>(undefined);

export const URL_REFRESH_KEY = 'etag';

export const AppNeedsRefreshProvider = ({ children }: { children: ReactNode }) => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const [documentHasFocus, setDocumentHasFocus] = useState(true);
  useEffect(() => {
    const onVisibilityChange = () => setDocumentHasFocus(!document.hidden);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  const indexMetadata = useRef<{
    etag: string | null;
    lastModified: string | null;
  }>(null);
  const lastRefreshed = useRef<Date>(null);

  const checkIfNeeded = useCallback(async () => {
    lastRefreshed.current = new Date();
    const res = await fetch('/', { method: 'HEAD', cache: 'no-store' });
    const etag = res.headers.get('etag');
    const lastModified = res.headers.get('last-modified');
    const updatedMetadata: (typeof indexMetadata)['current'] = { etag, lastModified };
    setUpdateAvailable(
      !strongIsEqual(updatedMetadata, indexMetadata.current) &&
        // If indexMetadata is null, we can assume the page is freshly loaded,
        // so the chance of a refresh needed is basically none
        indexMetadata.current !== null,
    );
    indexMetadata.current = updatedMetadata;
  }, []);

  useEffect(() => {
    if (!updateAvailable && documentHasFocus) {
      // When the document gains focus, go ahead and check if the app needs to be refreshed IF we haven't checked in the last 5 minutes
      if (lastRefreshed.current === null || differenceInMinutes(new Date(), lastRefreshed.current) >= 5) {
        setTimeout(
          () => checkIfNeeded(),
          // If this is the first load, we can assume we already have the latest app
          // so delay the call by 10s to give other API calls on the page time to complete first
          lastRefreshed.current === null ? 10_000 : 0,
        );
      }
      // Check every 5 minutes while the document has focus
      const interval = setInterval(checkIfNeeded, 1000 * 60 * 5);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [checkIfNeeded, updateAvailable, documentHasFocus]);

  const [searchParams, setSearchParams] = useSearchParams();
  const hardRefresh = useCallback(() => {
    setRefreshing(true);
    const hasQueryParams = searchParams.size > 0;
    window.location.replace(
      `${window.location.href}${hasQueryParams ? '&' : '?'}${URL_REFRESH_KEY}=${(updateAvailable ? indexMetadata.current?.etag : null) ?? uuid()}`,
    );
  }, [searchParams.size, updateAvailable]);

  useEffect(() => {
    if (searchParams.has(URL_REFRESH_KEY)) {
      searchParams.delete(URL_REFRESH_KEY);
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const value = useMemo(
    () => ({
      updateAvailable: updateAvailable && window.location.hostname !== 'localhost',
      hardRefresh,
    }),
    [hardRefresh, updateAvailable],
  );

  return (
    <AppNeedsRefreshContext value={value}>
      {children}
      <LoadingOverlay visible={refreshing} />
    </AppNeedsRefreshContext>
  );
};

export const useAppNeedsRefresh = () => useGeneralContext(AppNeedsRefreshContext, 'AppNeedsRefresh');
